'use client';

import { AcceptOrderTranslations } from './getTranslations';
import { acceptOrder } from '../../common/gqlActions/acceptOrder.action';
import { useEffect, useTransition } from 'react';
import { useRouter } from 'next/navigation';
import { useHandleGqlErrorsWithSnackbar } from '../../common/utils/errors/useHandleGqlErrorsWithSnackbar';
import { ButtonBase } from '../../common/components/ButtonBase/ButtonBase';
import { OrderOperation } from '../../orders/types';
import { css } from '@bts-web/utils-style-engine';
import { useClientTranslation } from '@bts-web/utils-lokalise';
import { useAppNotification } from '../../notifications/NotificationContextProvider';

const ASSET_NOT_ENABLED_FOR_AUTOMATED_ORDERS =
  'asset.not.enabled.for.automated.orders';

const INSUFFICIENT_ORDER_FUNDS = 'insufficient.order.funds';

interface CreateOrderButtonProps {
  assetId: string;
  orderId: string;
  translations: AcceptOrderTranslations;
  /** Pass order.operation so legacy redirect uses correct buy/sell finish path. */
  operation: OrderOperation;
}

export const CreateOrderButton = ({
  assetId,
  orderId,
  translations,
  operation,
}: CreateOrderButtonProps) => {
  const [isPending, startTransition] = useTransition();

  const router = useRouter();

  const { processErrors } = useHandleGqlErrorsWithSnackbar();

  const { t } = useClientTranslation();

  const { setAppNotification, closeNotificationById } = useAppNotification();

  useEffect(() => {
    return () => {
      closeNotificationById('asset-not-enabled-for-automated-orders');
    };
  }, []);

  const handleCreateOrder = () => {
    startTransition(async () => {
      const response = await acceptOrder(assetId, orderId, operation);

      if (response.errors) {
        if (response.errors.includes(ASSET_NOT_ENABLED_FOR_AUTOMATED_ORDERS)) {
          setAppNotification({
            title: t('info_toast_trading_for_asset_not_possible_title'),
            subtitle: t('info_toast_trading_for_asset_not_possible_subtitle'),
            visual: 'info',
            customDuration: 5000,
            position: 'bottomRight',
            id: 'asset-not-enabled-for-automated-orders',
            withClose: true,
          });

          return;
        }

        if (response.errors.includes(INSUFFICIENT_ORDER_FUNDS)) {
          router.push(
            `/assets/${assetId}/order/${orderId}/FAILED?reason=insufficient_funds`,
          );

          return;
        }

        processErrors(response.errors);

        return;
      }
    });
  };

  return (
    <div
      className={css({
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mx: 'auto',
        maxWidth: '440px',
      })}
    >
      <ButtonBase
        onClick={handleCreateOrder}
        disabled={isPending}
        isLoading={isPending}
        visual="primary"
        size="large"
      >
        {translations.acceptOrder}
      </ButtonBase>
    </div>
  );
};
